import React, { useState } from 'react';
import defaultTo from 'lodash/defaultTo';
import { useQuery } from '@apollo/client';
import { Flex } from '@atlaskit/primitives';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormHeader, FormFooter, RequiredAsterisk, Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { updateShipToParties, getShipToPartiesCountByCompanyName } from '@atlassian/temp-proxies';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  errorNotificationBuilder,
  ErrorComponents,
  InflightInvoiceBanner,
  NotificationTypes,
} from '@css-xp/base';
import {
  CommerceAddress,
  FetchShipToInPpcQuery,
  FetchShipToInPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SoldToAddressesType } from '@css-xp/ppc-select-customer-address';
import { failureMessage, HelpContent } from '@css-xp/ppc-common';
import { CCP_SEARCH_MAX_LIMIT } from '@css-xp/ppc-constants';
import { fetchShipToInPPC } from '@css-xp/ppc-sold-to-address-table';
import { TAX_UTILS } from '@css-xp/ppc-utils';
import { EditCustomerNamePaneContainer } from './edit-customer-name-pane.styles';

interface FormData {
  customerName: string;
}

interface Props {
  transactionAccountId: string;
  name: string;
  shipToAddresses?: SoldToAddressesType;
  onComplete?: (_responsePayload?: any) => void;
  inflightBannerMessage?: string;
  isSoldToAddressArchivalEnabled?: boolean;
  isDuplicateCustomerNameEnabled?: boolean;
}

export const EditCustomerNamePane = ({
  transactionAccountId,
  name,
  shipToAddresses,
  onComplete,
  inflightBannerMessage = 'All changes will be reflected to the orders included in the inflight invoice',
  isSoldToAddressArchivalEnabled,
  isDuplicateCustomerNameEnabled = false,
}: Props): JSX.Element => {
  const [customerName, setCustomerName] = useState(name);
  const [customerWithNameAlreadyExistsError, setCustomerWithNameAlreadyExistsError] = useState<string | null>(null);
  const [checkingCustomNameExists, setCheckingCustomNameExists] = useState(false);

  const { closeSidebar } = useSidebar();
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit({
    submitFormAction: updateShipToParties,
    onFailure: () => {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .withId('ship-to-party-failure')
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
    },
    detailedErrorMessage: (err) => {
      const baseMsg = err?.response?.data?.message ?? err?.message;
      const detailedMsg = err?.response?.data?.details?.errors?.details?.detailedDescriptions;
      return detailedMsg ? `${baseMsg}, ${detailedMsg}` : baseMsg;
    },
  });

  const ansGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const { data: soldToParties, loading: soldToPartiesLoading } = useQuery<
    FetchShipToInPpcQuery,
    FetchShipToInPpcQueryVariables
  >(fetchShipToInPPC, {
    variables: {
      searchParams: {
        companyName: name,
        transactionAccountId: transactionAccountId,
        ...(isSoldToAddressArchivalEnabled && { isActive: true }),
      },
      pageParams: {
        size: CCP_SEARCH_MAX_LIMIT,
      },
    },
    displayName: 'SoldToAddressForCompanyNameQuery',
    skip: typeof shipToAddresses !== 'undefined', // do not fetch if shipToAddresses is passed down from sidebar payload
    client: ansGraphqlClient,
  });

  const customerAddresses =
    typeof shipToAddresses !== 'undefined'
      ? shipToAddresses
      : (soldToParties?.shipToParty?.nodes as SoldToAddressesType);

  const getTaxIds = (address: CommerceAddress) => {
    if (!address?.taxIds?.length) return undefined;
    return TAX_UTILS.getTaxInformationFromAddress(address).map((taxId) => {
      return {
        displayName: taxId.taxDisplayName || undefined, // Added undefined to remove the display name from the taxId object in request payload
        label: taxId.label,
        id: taxId.value,
      };
    });
  };

  const handleFormSubmit = async (formData: FormData) => {
    // we need to restrict the customer name to be unique if duplicate customer name FF is not enabled
    if (!isDuplicateCustomerNameEnabled) {
      if (customerWithNameAlreadyExistsError) {
        setCustomerWithNameAlreadyExistsError(null);
      }

      try {
        setCheckingCustomNameExists(true);
        const { count } = await getShipToPartiesCountByCompanyName(transactionAccountId, formData?.customerName);
        if (count && count > 0) {
          setCustomerWithNameAlreadyExistsError(
            'Customer with the same name already exists. Please enter a different name.'
          );
          setCheckingCustomNameExists(false);
          return;
        }
      } catch (error: any) {
        setCheckingCustomNameExists(false);
        setCustomerWithNameAlreadyExistsError(error?.message);
        return;
      }
    }

    const requestData = {
      transactionAccountId,
      shipToAddresses: (customerAddresses ?? [])?.map((customerAddress) => ({
        id: customerAddress.id,
        name: defaultTo(formData?.customerName, '').trim(), // sending the updated name, & remove the leading and trailing spaces as it leads to new address creation for same name
        postalAddress: {
          line1: defaultTo(customerAddress.addressLine1 || null, ''),
          line2: defaultTo(customerAddress.addressLine2 || null, null),
          city: customerAddress.city ?? '',
          state: defaultTo(customerAddress.state || null, null),
          postcode: customerAddress.postcode ?? '',
          country: customerAddress.country ?? '',
        },
        pricingEligibility: customerAddress?.priceEligibility
          ? {
              [customerAddress.priceEligibility]: 'true',
            }
          : {
              commercial: 'true',
            },
        taxId: customerAddress?.taxId ?? null,
        taxIds: getTaxIds(customerAddress),
      })),
    };
    setCustomerName(defaultTo(formData?.customerName, '').trim());
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-shipTo-party-success')
      .withHeader('Update successful')
      .withMessage('Customer updated')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    if (onComplete) {
      onComplete({ customerName });
    }
  }

  const renderErrorMessage = (formError: string) => {
    const errorMessage = <ErrorComponents.ErrorMessageOnInfoPanel message={formError} helpContent={<HelpContent />} />;
    return <InfoPanel type="error" message={errorMessage} />;
  };

  return (
    <EditCustomerNamePaneContainer data-testid="customer-name-name-sidebar">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty }) => (
          <form {...formProps}>
            <FormHeader title="Edit name">
              <p aria-hidden="true">
                Required fields are marked with an asterisk <RequiredAsterisk />
              </p>
            </FormHeader>
            <InflightInvoiceBanner transactionAccountId={transactionAccountId} message={inflightBannerMessage} />
            {formError && <FormSection>{renderErrorMessage(formError)}</FormSection>}
            {customerWithNameAlreadyExistsError && (
              <InfoPanel type="error" message={customerWithNameAlreadyExistsError} />
            )}
            <FormSection>
              <div className="form-component-width">
                <Field
                  name="customerName"
                  id="customerName"
                  defaultValue={name}
                  label="Edit customer name"
                  isRequired={true}
                >
                  {({ fieldProps }) => <Textfield {...fieldProps} name="customerName" testId="customer-name" />}
                </Field>
              </div>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Flex>
                  <Button appearance="default" onClick={closeSidebar}>
                    Cancel
                  </Button>
                </Flex>
                <Flex>
                  <LoadingButton
                    isDisabled={!dirty || soldToPartiesLoading}
                    appearance="primary"
                    type="submit"
                    testId="update-button"
                    isLoading={isProcessing || checkingCustomNameExists}
                  >
                    Save
                  </LoadingButton>
                </Flex>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </EditCustomerNamePaneContainer>
  );
};

export default React.memo(EditCustomerNamePane);
